import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  buildCampaignPath, 
  buildTemplatePath, 
  buildViewPath, 
  buildSettingsPath,
  buildDocsPath,
  buildDashboardsPath,
  buildCampaignsPath,
  buildNewTabPath,
  parseUrlPath,
  DEFAULT_VIEW_MODE,
} from '../utils/routing';

/**
 * useUrlNavigation Hook
 * 
 * Provides URL-based navigation that syncs with app state.
 * Handles both programmatic navigation and browser back/forward.
 * 
 * This hook:
 * 1. Provides navigate* functions that update the URL
 * 2. Listens for URL changes and calls onUrlChange callback
 * 3. Manages the relationship between URL and app state
 * 
 * IMPORTANT: This hook distinguishes between:
 * - Programmatic navigation (from our code) - should NOT trigger callbacks
 * - Browser navigation (back/forward, direct URL) - SHOULD trigger callbacks
 */
export function useUrlNavigation({
  // Callbacks when URL changes (from browser navigation or initial load)
  onNavigateToCampaign,
  onNavigateToTemplate,
  onNavigateToView,
  onNavigateToDocs,
  onNavigateToDashboards,
  onNavigateToCampaigns,
  onNavigateToSettings,
  onNavigateToHome,
  onNavigateToBlank,
  // Callback for view mode changes (from browser back/forward)
  onViewModeChange,
  // Current state for building URLs
  defaultViewMode = DEFAULT_VIEW_MODE,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialMount = useRef(true);
  // Track programmatic navigations to skip the effect
  const skipNextUrlChange = useRef(false);
  // Track the last parsed location to detect actual changes
  const lastParsedLocation = useRef(null);
  // Track last URL string so callback identity changes don't re-trigger the effect
  const lastPathKeyRef = useRef(null);
  // Stable callback refs — effect only depends on location, not handler identity
  const callbacksRef = useRef({});
  callbacksRef.current = {
    onNavigateToCampaign,
    onNavigateToTemplate,
    onNavigateToView,
    onNavigateToDocs,
    onNavigateToDashboards,
    onNavigateToCampaigns,
    onNavigateToSettings,
    onNavigateToHome,
    onNavigateToBlank,
    onViewModeChange,
  };

  // ═══════════════════════════════════════════════════════════════
  // URL CHANGE HANDLER - Responds to browser navigation
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    const pathKey = `${location.pathname}|${location.search}`;
    const parsed = parseUrlPath(location.pathname, location.search);

    // Skip if this was a programmatic navigation (still record the URL we landed on)
    if (skipNextUrlChange.current) {
      skipNextUrlChange.current = false;
      lastPathKeyRef.current = pathKey;
      lastParsedLocation.current = parsed;
      return;
    }

    // Ignore re-runs when only callback identities changed, not the URL
    if (pathKey === lastPathKeyRef.current) {
      return;
    }
    lastPathKeyRef.current = pathKey;

    const lastParsed = lastParsedLocation.current;

    // On initial mount, let the startup hook handle navigation
    if (isInitialMount.current) {
      isInitialMount.current = false;
      lastParsedLocation.current = parsed;
      return;
    }

    lastParsedLocation.current = parsed;

    const {
      onNavigateToCampaign,
      onNavigateToTemplate,
      onNavigateToView,
      onNavigateToDocs,
      onNavigateToDashboards,
      onNavigateToCampaigns,
      onNavigateToSettings,
      onNavigateToHome,
      onNavigateToBlank,
      onViewModeChange,
    } = callbacksRef.current;

    // Check what changed
    const targetChanged = !lastParsed ||
      lastParsed.type !== parsed.type ||
      lastParsed.id !== parsed.id;

    const viewModeChanged = lastParsed &&
      lastParsed.viewMode !== parsed.viewMode;

    const customViewChanged = lastParsed &&
      lastParsed.customViewId !== parsed.customViewId;

    const missionIdChanged = lastParsed &&
      lastParsed.missionId !== parsed.missionId;

    // If target changed OR missionId changed, trigger full navigation
    // (missionId change within same campaign should re-focus the mission)
    if (targetChanged || missionIdChanged) {
      switch (parsed.type) {
        case 'campaign':
          onNavigateToCampaign?.(parsed.id, {
            viewMode: parsed.viewMode,
            missionId: parsed.missionId,
            customViewId: parsed.customViewId,
            isUrlDriven: true,
          });
          break;

        case 'template':
          onNavigateToTemplate?.(parsed.id, {
            viewMode: parsed.viewMode,
            isUrlDriven: true,
          });
          break;

        case 'view':
          onNavigateToView?.(parsed.id, {
            viewMode: parsed.viewMode,
            isUrlDriven: true,
          });
          break;

        case 'docs':
          onNavigateToDocs?.({ isUrlDriven: true });
          break;

        case 'dashboards':
          onNavigateToDashboards?.({ isUrlDriven: true });
          break;

        case 'campaigns':
          onNavigateToCampaigns?.({ isUrlDriven: true });
          break;

        case 'settings':
          onNavigateToSettings?.({ isUrlDriven: true });
          break;

        case 'blank':
          onNavigateToBlank?.({ isUrlDriven: true });
          break;

        case 'home':
        default:
          onNavigateToHome?.({ isUrlDriven: true });
          break;
      }
    } else if (viewModeChanged || customViewChanged) {
      // Same target but view mode or custom view changed (browser back/forward)
      onViewModeChange?.(parsed.viewMode, parsed.customViewId, parsed.missionId);
    }
  }, [location.pathname, location.search]);

  // ═══════════════════════════════════════════════════════════════
  // PROGRAMMATIC NAVIGATION
  // ═══════════════════════════════════════════════════════════════

  /**
   * Navigate to a campaign
   */
  const navigateToCampaign = useCallback((campaignId, options = {}) => {
    const { viewMode, missionId, customViewId, replace = false } = options;
    const path = buildCampaignPath(campaignId, viewMode || defaultViewMode, { missionId, customViewId });
    
    // Skip the URL change effect since we're handling navigation ourselves
    skipNextUrlChange.current = true;
    navigate(path, { replace });
  }, [navigate, defaultViewMode]);

  /**
   * Navigate to a template
   */
  const navigateToTemplate = useCallback((templateId, options = {}) => {
    const { viewMode, replace = false } = options;
    const path = buildTemplatePath(templateId, viewMode || defaultViewMode);
    
    skipNextUrlChange.current = true;
    navigate(path, { replace });
  }, [navigate, defaultViewMode]);

  /**
   * Navigate to a saved view
   */
  const navigateToView = useCallback((viewId, options = {}) => {
    const { viewMode, replace = false } = options;
    const path = buildViewPath(viewId, viewMode || defaultViewMode);
    
    skipNextUrlChange.current = true;
    navigate(path, { replace });
  }, [navigate, defaultViewMode]);

  /**
   * Navigate to the Doc library
   */
  const navigateToDocs = useCallback((options = {}) => {
    const { replace = false } = options;
    skipNextUrlChange.current = true;
    navigate(buildDocsPath(), { replace });
  }, [navigate]);

  const navigateToDashboards = useCallback((options = {}) => {
    const { replace = false } = options;
    skipNextUrlChange.current = true;
    navigate(buildDashboardsPath(), { replace });
  }, [navigate]);

  const navigateToCampaigns = useCallback((options = {}) => {
    const { replace = false } = options;
    skipNextUrlChange.current = true;
    navigate(buildCampaignsPath(), { replace });
  }, [navigate]);

  /**
   * Navigate to settings
   */
  const navigateToSettings = useCallback((options = {}) => {
    const { replace = false } = options;
    const path = buildSettingsPath();
    
    skipNextUrlChange.current = true;
    navigate(path, { replace });
  }, [navigate]);

  /**
   * Navigate to home
   */
  const navigateToHome = useCallback((options = {}) => {
    const { replace = false } = options;
    skipNextUrlChange.current = true;
    navigate('/', { replace });
  }, [navigate]);

  /**
   * Navigate to blank new-tab page
   */
  const navigateToNewTab = useCallback((options = {}) => {
    const { replace = false } = options;
    skipNextUrlChange.current = true;
    navigate(buildNewTabPath(), { replace });
  }, [navigate]);

  /**
   * Update just the view mode in the current URL (without triggering full navigation)
   * @param {string} newViewMode - The new view mode (flow, kanban, table, etc.)
   * @param {string} [customViewId] - Optional custom view ID to include in URL
   */
  const updateViewMode = useCallback((newViewMode, customViewId = undefined) => {
    const parsed = parseUrlPath(location.pathname, location.search);
    
    // If customViewId is not provided, keep the existing one from URL
    const effectiveCustomViewId = customViewId !== undefined ? customViewId : parsed.customViewId;
    
    let newPath;
    switch (parsed.type) {
      case 'campaign':
        newPath = buildCampaignPath(parsed.id, newViewMode, {
          missionId: parsed.missionId,
          customViewId: effectiveCustomViewId,
        });
        break;
      case 'template':
        newPath = buildTemplatePath(parsed.id, newViewMode);
        break;
      case 'view':
        newPath = buildViewPath(parsed.id, newViewMode);
        break;
      default:
        return; // Can't update view mode on home/settings
    }
    
    // Skip navigation if URL is already correct
    const currentPath = location.pathname + location.search;
    if (newPath === currentPath) return;
    
    // Skip the URL change effect - this is just a view mode update, not navigation
    skipNextUrlChange.current = true;
    // Use replace so view mode changes don't pollute history
    navigate(newPath, { replace: true });
  }, [navigate, location.pathname, location.search]);

  /**
   * Update the custom view ID in the current URL
   * @param {string} customViewId - The custom view ID (or null to clear)
   * @param {string} [viewMode] - Optional view mode to set at the same time
   */
  const updateCustomViewId = useCallback((customViewId, viewMode = undefined) => {
    const parsed = parseUrlPath(location.pathname, location.search);
    
    // Only works for campaigns (templates don't have custom views in this context)
    if (parsed.type !== 'campaign') return;
    
    const effectiveViewMode = viewMode !== undefined ? viewMode : parsed.viewMode;
    
    const newPath = buildCampaignPath(parsed.id, effectiveViewMode, {
      missionId: parsed.missionId,
      customViewId: customViewId || null, // null removes the param
    });
    
    // Skip navigation if URL is already correct
    const currentPath = location.pathname + location.search;
    if (newPath === currentPath) return;
    
    // Skip the URL change effect - this is just a custom view update
    skipNextUrlChange.current = true;
    // Use replace so custom view changes don't pollute history
    navigate(newPath, { replace: true });
  }, [navigate, location.pathname, location.search]);

  /**
   * Clear the mission ID from the current URL
   * Called after a mission has been focused to prevent re-focusing on view changes
   */
  const clearMissionId = useCallback(() => {
    const parsed = parseUrlPath(location.pathname, location.search);
    
    // Only campaigns have mission IDs
    if (parsed.type !== 'campaign' || !parsed.missionId) return;
    
    const newPath = buildCampaignPath(parsed.id, parsed.viewMode, {
      missionId: null, // Clear the mission
      customViewId: parsed.customViewId,
    });
    
    // Skip navigation if URL is already correct
    const currentPath = location.pathname + location.search;
    if (newPath === currentPath) return;
    
    // Skip the URL change effect
    skipNextUrlChange.current = true;
    // Use replace so this doesn't add a history entry
    navigate(newPath, { replace: true });
  }, [navigate, location.pathname, location.search]);

  /**
   * Get current parsed location
   */
  const getCurrentLocation = useCallback(() => {
    return parseUrlPath(location.pathname, location.search);
  }, [location.pathname, location.search]);

  return {
    // Navigation functions
    navigateToCampaign,
    navigateToTemplate,
    navigateToView,
    navigateToDocs,
    navigateToDashboards,
    navigateToCampaigns,
    navigateToSettings,
    navigateToHome,
    navigateToNewTab,
    updateViewMode,
    updateCustomViewId,
    clearMissionId,
    
    // Current location info
    getCurrentLocation,
    currentPath: location.pathname,
    currentSearch: location.search,
  };
}

export default useUrlNavigation;
